'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronDown, Menu, X } from 'lucide-react';
import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useScroll,
} from 'framer-motion';
import Image from 'next/image';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useSafeReducedMotion } from '@/hooks/useSafeReducedMotion';

/**
 * Product capability destinations surfaced in the header disclosure panel.
 * Every entry must be a real route; descriptions stay one line and plain.
 */
const productLinks = [
  {
    href: '/product',
    label: 'Product overview',
    description: 'Menus, ordering, kitchen, and growth on one platform',
  },
  {
    href: '/product/menus',
    label: 'Live menus',
    description: 'Branch menus that update the moment you edit',
  },
  {
    href: '/product/ordering',
    label: 'QR ordering',
    description: 'Guests browse, order, and pay from their own phones',
  },
  {
    href: '/product/pos',
    label: 'POS and kitchen',
    description: 'Counter checkout paired with a calm kitchen display',
  },
  {
    href: '/product/growth',
    label: 'Growth tools',
    description: 'Loyalty, gift cards, and promotions that bring guests back',
  },
  {
    href: '/product/analytics',
    label: 'Analytics',
    description: 'See what sells, when it sells, and at what margin',
  },
];

/** Flat evaluation-path links. */
const navLinks = [
  { label: 'Solutions', href: '/solutions' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Security', href: '/security' },
];

/** Secondary destinations: reachable in the drawer and footer, kept out of the sparse desktop bar. */
const secondaryLinks = [
  { label: 'About', href: '/about' },
  { label: 'Contact', href: '/contact' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Feedback', href: '/feedback' },
  { label: 'Help center', href: '/docs' },
];

const ease = [0.22, 1, 0.36, 1] as const;

function isActivePath(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}

export default function PublicHeader() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const mobileMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const mobileMenuPanelRef = useRef<HTMLElement>(null);
  const productTriggerRef = useRef<HTMLButtonElement>(null);
  const productPanelRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useSafeReducedMotion();

  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, 'change', (latest) => {
    setScrolled((previous) => {
      const next = latest > 50;
      return previous === next ? previous : next;
    });
  });

  /* Session-23 item 15: on the homepage the header starts over the ink hero,
     so it runs transparent with on-ink colours; any scroll (or any other
     route) returns it to the solid capsule. */
  const clearOverHero = pathname === '/' && !scrolled;
  const navPrimaryVar = clearOverHero ? 'var(--pub-on-ink-primary)' : 'var(--pub-text-primary)';
  const navSecondaryVar = clearOverHero ? 'var(--pub-on-ink-secondary)' : 'var(--pub-text-secondary)';

  // Any route change dismisses transient navigation surfaces.
  useEffect(() => {
    setMobileOpen(false);
    setProductOpen(false);
  }, [pathname]);

  // Lock page scroll behind the mobile drawer and the product panel: both are
  // modal surfaces with a scrim, so the page must not scroll under them.
  useEffect(() => {
    if (!mobileOpen && !productOpen) return;
    const previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = previousOverflow;
    };
  }, [mobileOpen, productOpen]);

  // Shared focus trap for both modal panels (mobile drawer + P8 product
  // panel): Escape closes and returns focus to the trigger; Tab wraps.
  useEffect(() => {
    const open = mobileOpen ? 'mobile' : productOpen ? 'product' : null;
    if (!open) return;

    const panel = open === 'mobile' ? mobileMenuPanelRef.current : productPanelRef.current;
    if (!panel) return;
    const focusable = () => Array.from(
      panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    );

    const items = focusable();
    items[0]?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (open === 'mobile') {
          setMobileOpen(false);
          mobileMenuTriggerRef.current?.focus();
        } else {
          setProductOpen(false);
          productTriggerRef.current?.focus();
        }
        return;
      }
      if (event.key !== 'Tab') return;
      const currentItems = focusable();
      if (currentItems.length === 0) return;
      const first = currentItems[0];
      const last = currentItems[currentItems.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen, productOpen]);

  const productActive = isActivePath(pathname, '/product');

  const desktopLinkClass = (active: boolean) =>
    cn(
      'flex h-11 items-center rounded-full px-4 text-sm transition-colors',
      active
        ? 'font-semibold'
        : 'font-medium hover:bg-[var(--pub-surface-base)]/60 hover:text-[var(--pub-text-primary)]',
      clearOverHero && !active && 'hover:bg-white/10 hover:text-[var(--pub-on-ink-primary)]',
    );

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 px-3 md:px-5">
        <motion.nav
          initial={reduceMotion ? false : { y: -80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease }}
          className={cn(
            'mnrq-container mt-3 flex items-center justify-between px-5 transition-all duration-300',
            scrolled ? 'h-14 rounded-xl' : 'h-16 rounded-2xl',
          )}
          style={{
            background: clearOverHero
              ? 'transparent'
              : scrolled
                ? 'var(--pub-surface-card)'
                : 'color-mix(in oklab, var(--pub-surface-card) 82%, transparent)',
            backdropFilter: clearOverHero ? undefined : 'blur(20px)',
            border: `1px solid ${clearOverHero ? 'transparent' : scrolled ? 'var(--pub-border-default)' : 'var(--pub-border-subtle)'}`,
            boxShadow: !clearOverHero && scrolled ? 'var(--pub-shadow-nav)' : 'none',
          }}
          aria-label="Main navigation"
        >
          {/* Logo */}
          <Link href="/" className="flex h-11 min-w-0 items-center gap-2.5" aria-label="Menuraq home">
            <Image
              src="/menuraq-monogram-transparent.png"
              alt=""
              width={32}
              height={32}
              priority
              className="h-8 w-8 shrink-0 rounded-xl object-cover"
            />
            <span
              className="hidden text-[1.05rem] font-semibold tracking-tight min-[420px]:inline lg:inline"
              style={clearOverHero ? { color: 'var(--pub-on-ink-primary)' } : undefined}
            >
              Menuraq
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden items-center gap-1 lg:flex">
            {/* Product disclosure → session-23 P8: a click-pinned, left-anchored
                panel that drops from the header with a page scrim behind it.
                Click-only on purpose — hover-opening a modal scrim dims the
                page for anyone just moving their pointer across the bar. */}
            <button
              ref={productTriggerRef}
              type="button"
              onClick={() => setProductOpen((open) => !open)}
              aria-expanded={productOpen}
              aria-haspopup="dialog"
              aria-controls="public-product-panel"
              aria-current={productActive ? 'true' : undefined}
              className={cn(
                'flex h-11 items-center gap-1.5 rounded-full px-4 text-sm transition-colors',
                productActive || productOpen
                  ? 'font-semibold'
                  : 'font-medium hover:bg-[var(--pub-surface-base)]/60 hover:text-[var(--pub-text-primary)]',
                clearOverHero &&
                  !(productActive || productOpen) &&
                  'hover:bg-white/10 hover:text-[var(--pub-on-ink-primary)]',
              )}
              style={{
                color:
                  productActive || productOpen
                    ? navPrimaryVar
                    : navSecondaryVar,
                background:
                  productActive || productOpen
                    ? clearOverHero
                      ? 'rgba(246, 232, 220, 0.14)'
                      : 'var(--pub-surface-base)'
                    : undefined,
              }}
            >
              Product
              <ChevronDown
                className={cn('h-4 w-4 transition-transform duration-200', productOpen && 'rotate-180')}
                aria-hidden="true"
              />
            </button>

            {navLinks.map((link) => {
              const active = pathname === link.href;
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  aria-current={active ? 'page' : undefined}
                  className={desktopLinkClass(active)}
                  style={{
                    color: active ? navPrimaryVar : navSecondaryVar,
                    background: active
                      ? clearOverHero
                        ? 'rgba(246, 232, 220, 0.14)'
                        : 'var(--pub-surface-base)'
                      : undefined,
                  }}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Desktop actions */}
          <div className="hidden items-center gap-2 lg:flex">
            <Button variant="ghost" className="min-h-11 text-sm font-medium" style={clearOverHero ? { color: 'var(--pub-on-ink-primary)' } : undefined} asChild>
              <Link href="/auth/signin">Sign in</Link>
            </Button>
            <Button
              className="min-h-11 text-sm font-medium"
              style={{ borderRadius: 'var(--pub-radius-pill)', background: 'var(--pub-ember-fill)', color: 'var(--pub-on-ink-primary)' }}
              asChild
            >
              <Link href="/auth/signup">Get Started</Link>
            </Button>
          </div>

          {/* Mobile actions */}
          <div className="flex items-center gap-1.5 lg:hidden">
            <Button
              ref={mobileMenuTriggerRef}
              variant="ghost"
              size="icon"
              className="h-11 w-11"
              style={clearOverHero ? { color: 'var(--pub-on-ink-primary)' } : undefined}
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              aria-expanded={mobileOpen}
              aria-controls="public-mobile-navigation"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </motion.nav>
      </header>

      {/* Session-23 P8: left-anchored product panel. ~610px white sheet drops
          from the header, page dims behind a scrim, explicit ✕ close, active
          items get an ink underline. Scrim sits below the bar (z-40 < header
          z-50) so the header stays interactive while pinned. */}
      <AnimatePresence>
        {productOpen ? (
          <>
            <motion.div
              className="fixed inset-0 z-40"
              style={{ background: 'rgba(0, 0, 0, 0.45)' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={reduceMotion ? { duration: 0 } : undefined}
              onClick={() => {
                setProductOpen(false);
                productTriggerRef.current?.focus();
              }}
              aria-hidden="true"
            />
            <motion.div
              ref={productPanelRef}
              id="public-product-panel"
              role="dialog"
              aria-modal="true"
              aria-label="Product navigation"
              initial={reduceMotion ? false : { opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? undefined : { opacity: 0, y: -12 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="fixed left-3 z-50 w-[min(610px,calc(100vw-1.5rem))] rounded-[var(--pub-radius-lg)] p-6 md:left-5"
              style={{
                top: scrolled ? '4.75rem' : '5.25rem',
                background: 'var(--pub-surface-card)',
                border: '1px solid var(--pub-border-default)',
                boxShadow: 'var(--pub-shadow-float)',
              }}
            >
              <div className="flex items-center justify-between">
                <span
                  className="text-[11px] font-medium uppercase tracking-[0.14em]"
                  style={{
                    color: 'var(--pub-text-muted)',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                  }}
                >
                  Product
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setProductOpen(false);
                    productTriggerRef.current?.focus();
                  }}
                  aria-label="Close menu"
                  className="flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-[var(--pub-surface-base)]"
                  style={{ color: 'var(--pub-text-secondary)' }}
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>

              <div className="mt-3 grid gap-x-4 gap-y-1 sm:grid-cols-2">
                {productLinks.map((item) => {
                  const active = pathname === item.href;
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      aria-current={active ? 'page' : undefined}
                      className="rounded-[var(--pub-radius-md)] p-3.5 transition-colors hover:bg-[var(--pub-surface-base)]/70"
                    >
                      <span
                        className={cn(
                          'block pb-0.5 text-sm',
                          active
                            ? 'w-fit font-semibold text-[var(--pub-text-primary)]'
                            : 'font-semibold text-[var(--pub-text-primary)]',
                        )}
                        style={
                          active
                            ? { boxShadow: 'inset 0 -2px 0 var(--pub-text-primary)' }
                            : undefined
                        }
                      >
                        {item.label}
                      </span>
                      <span
                        className="mt-1 block text-[13px] leading-snug"
                        style={{ color: 'var(--pub-text-secondary)' }}
                      >
                        {item.description}
                      </span>
                    </Link>
                  );
                })}
              </div>

              <div
                className="my-5 border-t"
                style={{ borderColor: 'var(--pub-border-subtle)' }}
                role="presentation"
              />

              <span
                className="text-[11px] font-medium uppercase tracking-[0.14em]"
                style={{
                  color: 'var(--pub-text-muted)',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                }}
              >
                Explore
              </span>
              <ul className="mt-2 columns-1 gap-8 sm:columns-2">
                {[...navLinks, ...secondaryLinks].map((link) => {
                  const active = pathname === link.href;
                  return (
                    <li key={link.href} className="break-inside-avoid">
                      <Link
                        href={link.href}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                          'block py-2 text-sm transition-colors',
                          active
                            ? 'font-semibold text-[var(--pub-text-primary)]'
                            : 'font-medium text-[var(--pub-text-secondary)] hover:text-[var(--pub-text-primary)]',
                        )}
                        style={
                          active
                            ? { boxShadow: 'inset 0 -2px 0 var(--pub-text-primary)' }
                            : undefined
                        }
                      >
                        {link.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen ? (
          <>
            <motion.div
              className="fixed inset-0 z-50"
              style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              aria-hidden="true"
            />
            <motion.aside
              ref={mobileMenuPanelRef}
              id="public-mobile-navigation"
              role="dialog"
              aria-modal="true"
              aria-labelledby="public-mobile-navigation-title"
              initial={reduceMotion ? { x: 0 } : { x: '100%' }}
              animate={{ x: 0 }}
              exit={reduceMotion ? { x: 0 } : { x: '100%' }}
              transition={{ duration: 0.28, ease }}
              className="fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col overflow-y-auto"
              style={{
                background: 'var(--pub-surface-card)',
                borderLeft: '1px solid var(--pub-border-subtle)',
              }}
            >
              <div className="flex items-center justify-between px-6 py-5">
                <Link href="/" className="flex items-center gap-2.5" onClick={() => setMobileOpen(false)}>
                  <Image
                    src="/menuraq-monogram-transparent.png"
                    alt=""
                    width={32}
                    height={32}
                    className="h-8 w-8 rounded-xl object-cover"
                  />
                  <span id="public-mobile-navigation-title" className="text-lg font-semibold tracking-tight">
                    Menuraq
                  </span>
                </Link>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-11 w-11" onClick={() => setMobileOpen(false)} aria-label="Close menu">
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              <div className="flex-1 px-6 py-4">
                <nav aria-label="Primary">
                  <ul className="space-y-1">
                    {[{ label: 'Product', href: '/product' }, ...navLinks].map((link) => {
                      const active = isActivePath(pathname, link.href);
                      return (
                        <li key={link.href}>
                          <Link
                            href={link.href}
                            onClick={() => setMobileOpen(false)}
                            aria-current={active ? 'page' : undefined}
                            className={cn(
                              'flex min-h-12 items-center rounded-[var(--pub-radius-md)] px-4 text-base transition-colors',
                              active
                                ? 'bg-[var(--pub-surface-base)] font-semibold'
                                : 'font-medium hover:bg-[var(--pub-surface-base)]/70',
                            )}
                            style={{ color: 'var(--pub-text-primary)' }}
                          >
                            {link.label}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </nav>

                <nav aria-label="Company" className="mt-6" style={{ borderTop: '1px solid var(--pub-border-subtle)' }}>
                  <p
                    className="mb-2 mt-5"
                    style={{
                      fontSize: 'var(--pub-micro-label)',
                      letterSpacing: 'var(--pub-micro-tracking)',
                      fontWeight: 'var(--pub-micro-weight)',
                      textTransform: 'var(--pub-micro-transform)' as never,
                      color: 'var(--pub-text-muted)',
                    }}
                  >
                    Company
                  </p>
                  <ul className="space-y-1">
                    {secondaryLinks.map((link) => {
                      const active = pathname === link.href;
                      return (
                        <li key={link.href}>
                          <Link
                            href={link.href}
                            onClick={() => setMobileOpen(false)}
                            aria-current={active ? 'page' : undefined}
                            className={cn(
                              'flex min-h-12 items-center rounded-[var(--pub-radius-md)] px-4 text-base transition-colors',
                              active
                                ? 'bg-[var(--pub-surface-base)] font-semibold'
                                : 'font-medium hover:bg-[var(--pub-surface-base)]/70',
                            )}
                            style={{ color: 'var(--pub-text-primary)' }}
                          >
                            {link.label}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </nav>
              </div>

              <div className="px-6 py-6" style={{ borderTop: '1px solid var(--pub-border-subtle)' }}>
                <div className="space-y-3">
                  <Button variant="outline" className="h-12 w-full" style={{ borderRadius: 'var(--pub-radius-pill)' }} asChild>
                    <Link href="/auth/signin" onClick={() => setMobileOpen(false)}>Sign in</Link>
                  </Button>
                  <Button className="h-12 w-full" style={{ borderRadius: 'var(--pub-radius-pill)' }} asChild>
                    <Link href="/auth/signup" onClick={() => setMobileOpen(false)}>Get Started</Link>
                  </Button>
                </div>
              </div>
            </motion.aside>
          </>
        ) : null}
      </AnimatePresence>
    </>
  );
}
